import { HookhouseFormData, MoodGroup, DEFAULT_RHYME_CONFIG, DEFAULT_STRUCTURE_CONFIG } from "./types";
import { VOCAL_PACKS } from "./vocalPacks";

export { VOCAL_PACKS };

export const HOOKHOUSE_PRO_SYSTEM_PROMPT = `
You are Big Chuck, the grizzled, no-nonsense proprietor of the Hookhouse, a legendary dive bar for songwriters. 
You are now operating on the **Hookhouse v2.6.1 (Core Prompt — Full-Band Realism Edition)**.

Your job is to take a user's flimsy song idea and forge it into a masterpiece, ready for the studio (Suno.ai V6/chirp-hawk). Your opponent is mediocrity. You are an expert in musical genres, lyrical storytelling, and high-fidelity production.

**CORE PHILOSOPHY (Full-Band Realism):**
Your goal is to describe a song not just as a genre, but as a specific recorded event. You must include details about:
- **Signal Chain:** Mention specific legendary mics (U87, SM57, MD421), tube preamps, and analog console saturation (Neve, SSL).
- **Room Acoustics:** Describe the recording space (high-ceiling wood room, tight iso-booth, natural stone reverb).
- **Performance Dynamics:** Instruct the "virtual band" on specific techniques (playing behind the beat, ghost notes on snare, finger-tapping on bass).
- **Sonic Textures:** Mention tape hiss, room bleed, and the "heat" of the tubes.

**V6 (CHIRP-HAWK) OPTIMIZATION:**
Suno V6's Style Box supports up to 1,000 characters and Lyrics support up to 5,000 characters. Leverage this space for maximum realism.

**PRODUCTION BLUEPRINT FORMATTING RULES:**
You MUST use a specific bracketed block format for the "STYLE (PRODUCTION BLUEPRINT)" section.
Format: [Category Name: Detailed description of gear, room, and technique.]
Example: [Drums (Room & Kit): Recorded in a high-ceiling timber room. 26" Ludwig Kick mic'd with a Neumann U47 FET...]

**YOUR TASK:**
1. **Deconstruct & Rebuild:** Rip the user's idea down to the studs and rebuild it with muscle and soul.
2. **Generate the Blueprint:** Produce the six required, labeled sections below.

**CRITICAL OUTPUT FORMATTING RULES:**
- You **MUST** output exactly six sections.
- Each section **MUST** start with its exact label (e.g., \`TITLE / ARTIST:\`) followed by a line break, and then a triple-backtick code block.
- **Production Intelligence Handling:** If a "Production Intelligence Filter" is provided in the user message, you MUST strictly adhere to those specific technical constraints (Key, Tempo, Instruments, Mix Positions) and incorporate them into your bracketed blocks.
- **STYLE (PRODUCTION BLUEPRINT) MUST** be a series of bracketed categories. DO NOT use Markdown bullet points or bold headers.
- **CRITICAL LYRICS RULE:** You MUST start the \`LYRICS\` block with the provided "{LAYER-1 VOCAL PHYSICS}" block exactly as written. 
- There must be **NO** commentary before, between, or after the sections.

---

**BEGIN STRICT OUTPUT FORMAT**

TITLE / ARTIST:
\`\`\`
(Creative title // fictional artist name — MUST use double-slash // as separator)
\`\`\`

SUMMARY:
\`\`\`
(1-3 sentence narrative summary)
\`\`\`

STYLE (PRODUCTION BLUEPRINT):
\`\`\`
(Introductory sentence about the vibe)

[Drums: Details...]

[Bass: Details...]

[Guitars: Details...]

[Vocal Chain: Details...]
\`\`\`

SUNO TAGS:
\`\`\`
(Technical machine tags. MAX 1,000 CHARACTERS.)
\`\`\`

EXCLUDED STYLES:
\`\`\`
(Styles/themes to avoid)
\`\`\`

LYRICS:
\`\`\`
(Vocal Physics block at the absolute top, followed by lyrics with structural tags. MAX 5,000 CHARACTERS.)

**CRITICAL SONG STRUCTURE RULES:**
- **Verses:** EXACTLY 4 lines each. NO MORE, NO LESS.
- **Chorus:** EXACTLY 4 lines. NO MORE, NO LESS.
- **Bridge:** 2-4 lines maximum.
- **Pre-Chorus (if used):** 2 lines maximum.
- DO NOT write 8-line verses or 8-line choruses. Standard song structure applies.
- If you violate these line counts, the blueprint will be rejected.
\`\`\`
`;

export const MUSIC_CRITIC_SYSTEM_PROMPT = `You are Big Chuck in "Critic Mode." You're a legendary Southern Rock producer with 30 years of sawdust on your boots and gold records on your wall. You speak with blue-collar authority and don't sugarcoat the truth.

MISSION: Analyze the provided song data (Production Architecture and Lyrics) and provide a technical, soul-focused critique in JSON format.

CRITICAL SCORING RULE: 
- DO NOT return 0 as a score unless the section is completely missing.
- Use a scale of 1-10.
- If the blueprint is high-fidelity, reward it. If it's lazy, tear it apart.

CRITIQUE PILLARS:
1. LYRICAL DEPTH: Look for cliches, forced rhymes, or genuine storytelling.
2. ARRANGEMENT: Is the instrumentation appropriate for the genre?
3. PRODUCTION REALISM: Does the signal chain and room acoustic modeling make sense?
4. AUTHENTICITY: Does it feel like a real record or an AI generic?

TONE: Brutally honest, gritty, technical, and steeped in Southern Rock history. Use metaphors related to whiskey, old trucks, the road, and analog gear. NO "AI" LANGUAGE. Speak like a human being who hates computers but loves music.`;

export const DEFAULT_FORM_VALUES: HookhouseFormData = {
  seed: "",
  blends: ["Americana", "Southern Rock"],
  stylePreset: "None",
  vocalPackKey: "None",
  selectedVoiceSpec: 'Lead',
  includeHarmonyStack: true,
  includeGangVocals: true,
  isInstrumental: false,
  mood: ["Dark", "Gritty"],
  explicit: false,
  povSetting: "",
  themesToHit: "",
  themesToAvoid: "",
  instruments: "",
  constraints: "",
  styleStrength: 85,
  weirdness: 65,
  songStructure: DEFAULT_STRUCTURE_CONFIG,
  rhymeScheme: DEFAULT_RHYME_CONFIG,
  styleMode: 'studio'
};

export const STYLE_PRESETS: { [key: string]: string } = {
  "Vessel of Woe": "Heavy Southern Sludge & Doom Metal. 70 BPM crushing stomp. Down-tuned 7-string guitars through Orange half-stacks, massive room compression on drums, ominous whispered layers.",
  "Boot Heel Gospel": "High-octane Southern Gospel & Soul. Stax-style brass section, rhythmic barrelhouse piano, massive choir-backed choruses, warm tube-saturated vocal chain.",
  "Neon Outlaw": "Modern Country Rock with 80s analog synth textures. Punchy drum machine/acoustic hybrid kit, clean Telecaster twang meeting lush Juno-60 pads.",
  "Dust & Redemption": "Intimate Folk Americana. Dry studio acoustics, fingerpicked mahogany acoustic guitar, close-mic'd vocal with audible breath details, subtle pedal steel swells.",
  "Strip Strip Swagger": "80s Sleaze Rock & Glam Metal. Gated reverb on snare, harmonized 'shred' guitar leads, high-register belting vocals, massive stereo spread.",
  "Swamp Ghost": "Atmospheric Delta Blues. Resonator guitar through a small cranked tube amp, muddy stomp-box percussion, deep swampy reverb, haunting harmonica moans.",
  "Radio Ready Anthem": "Polished 90s Heartland Rock. Mid-forward punchy mix, tight bass guitar pocket, layered acoustic and electric rhythms, pristine radio-ready vocal clarity.",
  "The Heavy Shuffle": "Hard Blues Rock Shuffle. Triplet drum groove with heavy ride cymbal, distorted electric piano, aggressive wah-wah guitar, raw high-energy performance dynamics.",
  "Funeral in Macon": "Dark Southern Gothic Ballad. Slow-tempo dirge, mournful cello, deep baritone vocals, church chapel acoustics, cinematic tension building to explosive finale.",
  "Georgia Groove": "Funk-infused Southern Rock. Slap bass foundation, syncopated rhythm guitars, punchy horn stabs, dry upfront vocals with rhythmic delivery.",
  "Midnight Rider [Redux]": "Southern Rock, Arena Rock, Gospel-Infused Power Soul. High-ceiling timber room acoustics, dual-Les Paul slide guitar attack, swirling Hammond B3 through a real Leslie cabinet.",
  "MY Hometown": "Genre:\"Southern Rock, Southern Metal, Groove Metal\", Style:\"Gritty Southern Groove, Warm-Gritty, Defiant-Soulful\", Voice:\"Male Southern Rock Baritone, Deep, Gritty, Soulful; Map:Intro(Spoken)→Vrs(Raspy)→Pre(Building)→Ch(Belt)\", Instr:\"EGtr(Rhy+Ld), 12-Str AGtr, Slide Gtr, Bass, B3, Piano, Pads, LStr, Drums(K/S/T/H/R/C), Shaker, Tamb, FX\", Design:\"Intro:CleanGtr(RoomVrb)+B3(SlowLsl); Vrs:AGtr(dry)+B3(vib)+Shfl(dry); Pre:CrunchedGtrs(TpSat)+RiserFX; Ch:TwinGtrs(wide)+PwrKit(PltVrb)+B3(FastLsl); Post:LdFill(dly); Brdg:LPno(DrkVrb)+Solo:ODLd(wah); Outro:CleanGtr+B3(fade)\", FX:\"Intro:Room; Vrs:ShtPlt; Pre:TpSat; Ch:WideHl; Post:Dly; Brdg:DrkVrb; Solo:Dly; Outro:Hl\", Dyn:\"Intro:Soft, Vrs:Med, Pre:Rise, Ch:Peak, Post:Lift, Brdg:Drop, Solo:Max, Outro:Fade\", Arr:\"Intro→V1→Pre→Ch→V2→Pre→Ch→Bridge→Solo→FinalCh→Outro\"",
  "Southern Groove Metal, Progressive Djent, Rock Power Ballad": "[Production: Studio Grade, 48kHz, 24-bit Fidelity], [Mastering: Crystal Clear, Wide Stereo Imaging], Southern Groove Metal, Progressive Djent, Rock Power Ballad, 130 BPM, Key-E-Minor, [Vocal: Gritty Texas Baritone, Raspy Powerhouse Tenor, Jorn Lande Style, High-Octave Belts, Heavy Vocal Fry], [Texture: Analog Tape Saturation, Tube Warmth], [Guitars: 7-String, Thick Palm Mutes, Pinch Harmonics, High-Gain Shred Solos], [Drums: Tight Punchy Snare, Rhythmic Double Bass, Clear Room Reverb], [Mix: Impactful Transients, Deep Defined Low End, Dynamic Surge], Piano, Modulation: D-minor To Eb-major, Massive Cinematic Shift, ‑lo-fi, ‑muffled, ‑mono, ‑neon, ‑digital souls, ‑clockwork, ‑symphony of stars, ‑thin mix, ‑synthesized drums, ‑orchestral, ‑whispers, ‑pop-punk, ‑acoustic guitar focus, ‑echoes of the past, ‑bright happy themes",
  "Thunder-Strut": "Southern rock vocal anthem, hard-driving riff rock, 128 BPM, 4/4 time, key of E major, Male mid-range vocal, conversational low verses building to confident full-voiced chorus belt, Overdriven electric guitar riff leads with thick humbucker crunch, tube amp saturation, Dual guitar arrangement — rhythm palm-muted and locked to kick, lead plays answering licks between vocal phrases, Aggressive fingerstyle bass with warm midrange growl, Big open rock drums — punchy kick, cracking snare with room ambience, loose hi-hat, Hammond organ buried for warmth, Cowbell and tambourine for texture, Spoken near-whisper breakdown section over stripped drums and bass, Extended instrumental guitar solo with wah pedal, melodic bends building to aggressive climax, Analog warmth, room mics, wide stereo anthem sections, Call-and-response between vocal and riff throughout, No vocal effects or processing",
  "Hickra Stick": "Blend: Southern Rock / Groove / Boogie / Jam Band, BPM: 96–102 (feels mid-tempo, chorus pushes slightly), Time: 4/4 with syncopated 3+3+2 feel inside bars, Key center: D minor (drop-D flavor), Guitars: tight, drop-tuned crunch; RIFF A = 3+3+2 palm-mute mantra; RIFF B = chorus open-chord answer; RIFF C = short slide-up fill, Drums: fat kick, backbeat crack; half-time in verses, full-time in chorus, Bass: locks the riff, occasional octave jumps, Arc: restrained verse → lift pre → slam chorus → spacey bridge → stacked final chorus → hard stop, No shreddy solos; no busy hi-hat patterns; no pop sheen; no EDM risers; no trap hats; no djent stutters; no ska upstrokes; no 808 sub drops; no auto-tune warble; no reverb-wash that masks the riff; avoid lyrical clichés and overlong lines that break the pocket",
};

export const MOOD_GROUPS: MoodGroup[] = [
  {
    name: "Dark & Heavy",
    options: [
      { label: "Ominous", description: "Dread-building tension" },
      { label: "Menacing", description: "Threatening energy" },
      { label: "Crushing", description: "Weight and power" },
      { label: "Haunting", description: "Eerie undertones" },
      { label: "Dark", description: "Shadowy and brooding" },
      { label: "Gritty", description: "Raw, unpolished texture" },
      { label: "Aggressive", description: "High-intensity confrontation" },
      { label: "Oppressive", description: "Heavy, inescapable weight" },
      { label: "Angry", description: "Pure combustible heat" },
      { label: "Brooding", description: "Slow-burn darkness simmering under the surface" },
      { label: "Savage", description: "Primal, unrestrained ferocity" },
      { label: "Vengeful", description: "Cold-blooded retribution energy" }
    ]
  },
  {
    name: "Triumphant & Emotional",
    options: [
      { label: "Anthemic", description: "Fist-in-the-air moments" },
      { label: "Epic", description: "Cinematic scale and grandeur" },
      { label: "Triumphant", description: "Victory energy" },
      { label: "Nostalgic", description: "Bittersweet memory" },
      { label: "Longing", description: "Ache and yearning" },
      { label: "Melodic", description: "Focus on singing hooks" },
      { label: "Harmonic", description: "Rich vocal layering" },
      { label: "Melancholy", description: "Beautiful sadness, minor-key ache" },
      { label: "Sorrowful", description: "Deep grief, soul-heavy lament" },
      { label: "Desperate", description: "Last-chance urgency, nothing left to lose" }
    ]
  },
  {
    name: "Texture & Feel",
    options: [
      { label: "Smoky", description: "Late-night bar vibe" },
      { label: "Groovy", description: "Pocket-locked feel" },
      { label: "Defiant", description: "Middle-finger energy" },
      { label: "Reverent", description: "Gospel-tinged spiritual" },
      { label: "Clean", description: "Transparent studio clarity" },
      { label: "Radio", description: "Polished, commercial sheen" },
      { label: "Raw", description: "Immediate and unfiltered" },
      { label: "Swampy", description: "Thick, humid bayou textures" },
      { label: "Blues-Heavy", description: "Soul-baring lead focus" },
      { label: "Driving", description: "Forward rhythmic momentum" },
      { label: "Energetic", description: "High-octane performance" },
      { label: "Hypnotic", description: "Trance-inducing repetition and drone" },
      { label: "Reckless", description: "Loose cannon, damn-the-consequences chaos" },
      { label: "Rebellious", description: "Anti-authority outlaw spirit" },
      { label: "Theatrical", description: "Over-the-top dramatic performance" }
    ]
  },
  {
    name: "Performance Mode",
    options: [
      { label: "Locked-In Riff Cycles", description: "Tight repetitive riff loops, zero drift" },
      { label: "Precision Chops", description: "Technical execution, every note intentional" },
      { label: "Power Groove Lockdown", description: "Bass and drums welded together in the pocket" },
      { label: "Deliberate Pacing", description: "Slow, measured delivery — every beat earns its space" },
      { label: "Loose Jams", description: "Improvisational feel, Allman/Dead-style breathing room" },
      { label: "Drunken Groove Swagger", description: "Sloppy-cool stumble, outlaw barroom energy" },
      { label: "Behind-the-Beat Drag", description: "Lazy pull on the tempo, blues-heavy laid-back feel" },
      { label: "Ragged-but-Right Feel", description: "Rough edges left in, punk honesty over polish" },
      { label: "Laid-Back Pocketing", description: "Deep-in-the-groove relaxed soul/funk timing" },
      { label: "Push-Pull Cadence", description: "Tension between rushing and dragging, human feel" },
      { label: "Explosive Downstroke Chugs", description: "Full-arm palm-mute attack, Pantera stomp" },
      { label: "Palm-Muted Momentum", description: "Chugging eighth-note drive, djent/metal freight train" },
      { label: "Start-Stop Chaos", description: "Breakdowns and stabs with silence as a weapon" },
      { label: "Drone-Sustained Expression", description: "Held notes and feedback, doom/stoner atmosphere" },
      { label: "Trap Bounce", description: "Triplet hi-hat driven 808 pocket, head-nod swagger" },
      { label: "Boom Bap Swing", description: "Laid-back snare hits, classic head-nod groove" },
      { label: "Double-Time Flow", description: "Rapid-fire syllable delivery, Eminem/Tech N9ne precision" },
      { label: "Chopped & Screwed", description: "Slowed-down syrup tempo, DJ Screw Memphis drag" },
      { label: "Four-on-the-Floor Drive", description: "Relentless kick pulse, no breaks, rave endurance" },
      { label: "Build-and-Drop", description: "Tension riser into massive bass drop, festival energy" },
      { label: "Glitch Stutter", description: "Chopped micro-edits, rhythmic glitch patterns, IDM precision" },
      { label: "Sidechain Pump", description: "Ducking compression groove, breathing pads, classic EDM throb" }
    ]
  },
  {
    name: "Electronic & Rave",
    options: [
      { label: "Euphoric", description: "Hands-in-the-air festival bliss, pure ecstasy" },
      { label: "Dystopian", description: "Dark future cityscapes, industrial decay" },
      { label: "Cosmic", description: "Deep space exploration, celestial wonder" },
      { label: "Neon", description: "Electric nightlife glow, synthwave nostalgia" },
      { label: "Trippy", description: "Mind-bending psychedelic distortion, altered states" },
      { label: "Mechanical", description: "Robotic precision, cold machine aesthetic" },
      { label: "Ethereal", description: "Floating, weightless, dream-state atmosphere" },
      { label: "Underground", description: "Warehouse rave, raw unpolished club energy" },
      { label: "Futuristic", description: "Cutting-edge sonic experimentation, tomorrow's sound" },
      { label: "Frenetic", description: "Manic energy, breakneck tempo, sensory overload" }
    ]
  },
  {
    name: "Street & Urban",
    options: [
      { label: "Flexing", description: "Confident swagger and success energy" },
      { label: "Cocky", description: "Untouchable bravado, top-of-the-world attitude" },
      { label: "Paranoid", description: "Looking over your shoulder, trust nobody" },
      { label: "Hype", description: "Turn-up energy, crowd-moving intensity" },
      { label: "Cold", description: "Emotionless calculation, clinical delivery" },
      { label: "Wavy", description: "Smooth, effortless cool flow" },
      { label: "Introspective", description: "Internal reflection and self-examination" },
      { label: "Hustling", description: "Grind mentality, come-up ambition" },
      { label: "Luxurious", description: "Opulent, champagne-soaked excess" },
      { label: "Streetwise", description: "Hard-earned wisdom from the block" }
    ]
  },
  {
    name: "Pop & Romantic",
    options: [
      { label: "Sultry", description: "Seductive warmth, bedroom-voice intimacy" },
      { label: "Heartbroken", description: "Post-breakup devastation, mascara-running pain" },
      { label: "Playful", description: "Flirty, fun, carefree summer energy" },
      { label: "Vulnerable", description: "Stripped-down emotional honesty, no armor" },
      { label: "Sassy", description: "Confident attitude, sharp-tongued wit" },
      { label: "Bittersweet", description: "Happy melody hiding sad truth, smile through tears" },
      { label: "Sensual", description: "Slow-burn physical chemistry, R&B heat" },
      { label: "Empowered", description: "Self-worth anthem, standing tall energy" },
      { label: "Dreamy", description: "Head-in-the-clouds romantic fantasy" },
      { label: "Toxic", description: "Addictive bad-for-you relationship, can't-quit pull" }
    ]
  }
];

export const MOOD_OPTIONS = []; // Deprecated in favor of MOOD_GROUPS